/**
 *
 * @module account
 *
 */

import Check from 'check-types';

import UserProfile from './userProfile';
import Identity from './identity';

import InvalidArgumentException from './../exception/invalidArgumentException';

const {
    object,
    array,
    nonEmptyString,
    instanceStrict,
    nonEmptyObject
} = Check.assert;

/**
 *
 * @class Account
 * @desc Represents the account information associated with a given a user.
 *
 */
export default class Account {

    /**
     *
     * @param {Object} options
     * @param {String} options.accountId - the account ID.
     * @param {UserProfile} options.activeUserProfile
     * @param {Object} [options.attributes={}] - the account attributes which can be modified and saved.
     * @param {Array<Identity>} [options.identities=[]] an array of associated Identities
     * @throws {InvalidArgumentException}
     *
     */
    constructor(options = {}) {

        const errorMessage = {
            ctor: 'Account(options)',
            get object() {
                return `${this.ctor} options should be a non-empty Object`;
            },
            get accountId() {
                return `${this.ctor} options.accountId should be a String with length`;
            },
            get activeUserProfile() {
                return `${this.ctor} options.activeUserProfile should be an instance of SDK.Services.Account.UserProfile`; // eslint-disable-line max-len
            },
            get attributes() {
                return `${this.ctor} options.attributes should be an Object`;
            },
            get identities() {
                return `${this.ctor} options.identities should be an Array`;
            }
        };

        nonEmptyObject(options, errorMessage.options, InvalidArgumentException);

        const { accountId, activeUserProfile, attributes = {}, identities = [] } = options;

        nonEmptyString(accountId, errorMessage.accountIdError, InvalidArgumentException);
        instanceStrict(activeUserProfile, UserProfile, errorMessage.activeUserProfile, InvalidArgumentException);

        if (Check.assigned(attributes)) {
            object(attributes, errorMessage.attributes, InvalidArgumentException);
        }

        if (Check.assigned(identities)) {
            array(identities, errorMessage.identities, InvalidArgumentException);
        }

        /**
         *
         * @access public
         * @type {String}
         * @desc Gets the ID of the account.
         *
         */
        this.accountId = accountId;

        /**
         *
         * @access public
         * @since 3.5.0
         * @type {UserProfile}
         *
         */
        this.activeProfile = activeUserProfile;

        /**
         *
         * @access public
         * @type {Object}
         * @desc Gets the attributes of an account that may be returned by the service.
         *
         */
        this.attributes = attributes;

        /**
         *
         * @access public
         * @since 3.5.0
         * @type {Array<Identity>}
         *
         */
        this.identities = identities.map((identity) => new Identity(identity.identityId, identity.providerId));

    }

}



// WEBPACK FOOTER //
// ./src/services/account/account.js